import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DataScope } from '../interfaces/dashboard.component';

// Small pill that states who a KPI/section's data covers: the whole clinic,
// just the signed-in user, or both. Distinguished by icon + label (not colour),
// so it stays on the teal theme.
@Component({
  selector: 'app-scope-chip',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="scope-chip" [title]="tooltip">
      <mat-icon>{{ icon }}</mat-icon>
      {{ label }}
    </span>
  `,
  styles: [
    `
      .scope-chip {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.01em;
        padding: 2px 8px;
        border-radius: 20px;
        background: var(--teal-bg);
        color: var(--teal-dark);
        white-space: nowrap;
        vertical-align: middle;
      }
      .scope-chip mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
        line-height: 14px;
      }
    `,
  ],
})
export class ScopeChipComponent {
  @Input() scope: DataScope = 'clinic';

  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    // OnPush won't re-check this component on a language switch (its `scope`
    // input doesn't change), so the label getter would keep the old text.
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
  }

  get label(): string {
    if (this.scope === 'user') return this.translate.instant('GUI.dashboard.labels.chips.user');
    if (this.scope === 'clinicUser') return 'Clinic + you';
    return this.translate.instant('GUI.dashboard.labels.chips.clinic');
  }

  get icon(): string {
    return this.scope === 'user' ? 'person' : 'groups';
  }

  get tooltip(): string {
    if (this.scope === 'user') return 'Shows only your data';
    if (this.scope === 'clinicUser') return 'Clinic-wide, with a per-user view';
    return 'Clinic-wide data';
  }
}
